import axios from "axios";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
}

function pick(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

/**
 * @param {object} row
 * @returns {import('../types/carrierPartner.types').CarrierPartnerRow}
 */
export function normalizeCarrierPartner(row = {}) {
  const id = row.id ?? row.Id ?? null;
  const parcelId = row.parcelId ?? row.ParcelId ?? null;
  const chargeRaw =
    row.charge ??
    row.Charge ??
    row.price ??
    row.Price ??
    row.parcelCharge ??
    row.ParcelCharge;
  const chargeNum = Number(chargeRaw);
  return {
    id,
    parcelId,
    recordSerialNo: pick(
      row.recordSerialNo,
      row.RecordSerialNo,
      row.bookingNo,
      row.BookingNo,
    ),
    senderName: pick(row.senderName, row.SenderName),
    detailsName: pick(
      row.detailsName,
      row.DetailsName,
      row.detailName,
      row.DetailName,
    ),
    carrierPartnerPointId: Number(
      row.carrierPartnerPointId ?? row.CarrierPartnerPointId ?? 0,
    ) || null,
    pointName: pick(row.pointName, row.PointName),
    carrierPartnerName: pick(
      row.carrierPartnerName,
      row.CarrierPartnerName,
      row.carrierName,
      row.CarrierName,
    ),
    charge: Number.isFinite(chargeNum) ? chargeNum : 0,
    trackingLink: pick(
      row.trackingLink,
      row.TrackingLink,
      row.parcelTrackingLink,
      row.ParcelTrackingLink,
    ),
    creationDate: pick(
      row.creationDate,
      row.CreationDate,
      row.createdAt,
      row.CreatedAt,
      row.date,
      row.Date,
    ),
    modificationDate: pick(
      row.modificationDate,
      row.ModificationDate,
      row.updatedAt,
      row.UpdatedAt,
    ),
    creatorId: pick(row.creatorId, row.CreatorId),
    modifierId: pick(row.modifierId, row.ModifierId),
  };
}

/**
 * @param {object} row
 * @returns {import('../types/carrierPartner.types').CarrierPartnerParcelOption|null}
 */
export function normalizeParcelOption(row = {}) {
  const parcelId = row.parcelId ?? row.ParcelId ?? row.parcelNo ?? row.ParcelNo;
  if (parcelId == null || parcelId === "") return null;
  const routeIdRaw = row.routeId ?? row.RouteId ?? row.routingTypeId ?? row.RoutingTypeId;
  const routeIdNum = Number(routeIdRaw);
  return {
    parcelId: Number(parcelId),
    recordSerialNo: pick(
      row.recordSerialNo,
      row.RecordSerialNo,
      row.bookingNo,
      row.BookingNo,
    ),
    senderName: pick(row.senderName, row.SenderName),
    routeId: Number.isFinite(routeIdNum) && routeIdNum > 0 ? routeIdNum : null,
    routeName: pick(
      row.routeName,
      row.RouteName,
      row.routingTypeName,
      row.RoutingTypeName,
    ),
  };
}

/**
 * Resolve a parcel's route when search results do not include it (edit, or older API).
 * @param {number|string} parcelId
 * @returns {Promise<{routeId: number|null, routeName: string}>}
 */
export async function getParcelRouteForCarrierPartner(parcelId) {
  if (parcelId == null || parcelId === "") {
    return { routeId: null, routeName: "" };
  }
  try {
    const options = await searchCarrierPartnerParcels(String(parcelId));
    const match = options.find(
      (opt) => Number(opt.parcelId) === Number(parcelId),
    );
    if (match?.routeName || match?.routeId) {
      return {
        routeId: match.routeId || null,
        routeName: match.routeName || "",
      };
    }
  } catch {
    /* fall through */
  }

  try {
    const { data } = await axios.get(
      `${getApiBaseUrl()}TransitionCost/GetParcelProductsForEdit/${parcelId}`,
      { headers: getAuthHeaders() },
    );
    const products = unwrapList(data);
    const first = products[0] || {};
    const routeIdRaw = first.routeId ?? first.RouteId ?? first.routingTypeId;
    const routeIdNum = Number(routeIdRaw);
    const routeId =
      Number.isFinite(routeIdNum) && routeIdNum > 0 ? routeIdNum : null;
    const nestedName = pick(
      first.routeName,
      first.RouteName,
      first.routingTypeName,
    );
    if (nestedName) return { routeId, routeName: nestedName };
    if (!routeId) return { routeId: null, routeName: "" };

    const { data: routes } = await axios.get(`${getApiBaseUrl()}Route`, {
      headers: getAuthHeaders(),
    });
    const row = unwrapList(routes).find(
      (r) => String(r.id ?? r.Id ?? r.routeId) === String(routeId),
    );
    return {
      routeId,
      routeName: pick(row?.routeName, row?.RouteName, row?.name, row?.Name),
    };
  } catch {
    return { routeId: null, routeName: "" };
  }
}

export async function getCarrierPartners() {
  const { data } = await axios.get(`${getApiBaseUrl()}CarrierPartner`, {
    headers: getAuthHeaders(),
  });
  return unwrapList(data).map(normalizeCarrierPartner);
}

export async function getCarrierPartnerById(id) {
  if (id == null || id === "") return null;
  const { data } = await axios.get(`${getApiBaseUrl()}CarrierPartner/${id}`, {
    headers: getAuthHeaders(),
  });
  if (!data || typeof data !== "object") return null;
  return normalizeCarrierPartner(data);
}

export async function getCarrierPartnersByParcelId(parcelId) {
  if (parcelId == null || parcelId === "") return [];
  const { data } = await axios.get(
    `${getApiBaseUrl()}CarrierPartner/GetByParcelId/${parcelId}`,
    { headers: getAuthHeaders() },
  );
  return unwrapList(data).map(normalizeCarrierPartner);
}

export async function searchCarrierPartnerParcels(query) {
  const q = String(query || "").trim();
  if (q.length < 2) return [];
  const { data } = await axios.get(
    `${getApiBaseUrl()}CarrierPartner/SearchParcels`,
    {
      params: { q },
      headers: getAuthHeaders(),
    },
  );
  return unwrapList(data)
    .map(normalizeParcelOption)
    .filter(Boolean);
}

/**
 * @param {import('../types/carrierPartner.types').CarrierPartnerPayload} payload
 */
export async function createCarrierPartner(payload) {
  const { data } = await axios.post(
    `${getApiBaseUrl()}CarrierPartner`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    },
  );
  return data;
}

/**
 * @param {number|string} id
 * @param {import('../types/carrierPartner.types').CarrierPartnerPayload} payload
 */
export async function updateCarrierPartner(id, payload) {
  const { data } = await axios.put(
    `${getApiBaseUrl()}CarrierPartner/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    },
  );
  return data;
}

export async function deleteCarrierPartner(id, modifierId) {
  await axios.delete(`${getApiBaseUrl()}CarrierPartner/${id}`, {
    params: modifierId ? { modifierId } : undefined,
    headers: getAuthHeaders(),
  });
}

/**
 * @param {object} row
 * @returns {import('../types/carrierPartner.types').CarrierPartnerPoint}
 */
export function normalizeCarrierPartnerPoint(row = {}) {
  const id = Number(row.id ?? row.Id ?? 0);
  const isActiveRaw = row.isActive ?? row.IsActive;
  return {
    id,
    name: pick(row.name, row.Name),
    isActive: isActiveRaw === false || isActiveRaw === 0 ? false : true,
  };
}

export async function getCarrierPartnerPoints() {
  const { data } = await axios.get(`${getApiBaseUrl()}CarrierPartner/Points`, {
    headers: getAuthHeaders(),
  });
  return unwrapList(data)
    .map(normalizeCarrierPartnerPoint)
    .filter((p) => p.id);
}

export async function getActiveCarrierPartnerPoints() {
  const { data } = await axios.get(
    `${getApiBaseUrl()}CarrierPartner/Points/Active`,
    { headers: getAuthHeaders() },
  );
  return unwrapList(data)
    .map(normalizeCarrierPartnerPoint)
    .filter((p) => p.id);
}

export async function createCarrierPartnerPoint(payload) {
  const { data } = await axios.post(
    `${getApiBaseUrl()}CarrierPartner/Points`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    },
  );
  return data;
}

export async function updateCarrierPartnerPoint(id, payload) {
  const { data } = await axios.put(
    `${getApiBaseUrl()}CarrierPartner/Points/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    },
  );
  return data;
}

export async function deleteCarrierPartnerPoint(id, modifierId) {
  await axios.delete(`${getApiBaseUrl()}CarrierPartner/Points/${id}`, {
    params: modifierId ? { modifierId } : undefined,
    headers: getAuthHeaders(),
  });
}
